import React, { useCallback } from 'react';
import styled from 'styled-components';
import mapboxgl from 'mapbox-gl';
import { useSelector, useDispatch } from 'react-redux';
import { FaMap } from 'react-icons/fa';

import { Key } from './key';
import { mapState, getPrintableMapbox } from './selectors';
import { Layers } from './layers';
import mapStyleJSON from './map-style.json';

const Container = styled.div`
  flex: 1;
  position: relative;
  height: 100%;
  width: 100%;
`;

const ToggleSatellite = styled.div`
  cursor: pointer;
  position: absolute;
  right: 10px;
  top: 150px;
  padding: 7px;
  background-color: white;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
  height: 16px;
  width: 16px;
  border-radius: 4px;
`;

const containerStyle = {
  height: '100%',
  width: '100%',
};

/* eslint-disable react/style-prop-object */

export const MapPrint = ({ id = 'PRINT_MAP' }) => {
  const {
    layers,
    tileSourceOptions,
    filters,
    zoom,
    center,
    hasNewLayers,
    mapStyle,
    selectedSurveyLayers,
  } = useSelector(mapState);

  const Mapbox = useSelector(getPrintableMapbox);

  const dispatch = useDispatch();

  const onMoveEnd = useCallback(
    () => {
      dispatch({ type: 'CLEAR_LOADING_LAYERS' });
    },
    [dispatch],
  );

  const onDataLoading = useCallback(
    (_, layer) => {
      if (
        layer.sourceId === 'composite'
      ) {
        return null;
      }
      if (!layer.sourceId || layer.isSourceLoaded) return null;
      dispatch({
        type: 'START_LOADING_LAYER',
        payload: layer.sourceId,
      });
      return null;
    },
    [dispatch],
  );

  const onData = useCallback(
    (map, layer) => {
      if (
        layer.sourceId === 'composite'
      ) return;
      if (!layer.sourceId || !layer.isSourceLoaded) return;
      if (map.getLayer('road_layer-layer-raster')) map.moveLayer('road_layer-layer-raster');
      if (map.getLayer('road_label_layer-layer-raster')) map.moveLayer('road_label_layer-layer-raster');
      dispatch({
        type: 'STOP_LOADING_LAYER',
        payload: layer.sourceId,
      });
    },
    [dispatch],
  );

  const onStyleLoad = useCallback(
    (map, ...args) => {
      // eslint-disable-next-line no-param-reassign
      map.getCanvas().style.cursor = 'pointer';
      map.jumpTo({
        zoom,
        center,
      });

      map.addControl(new mapboxgl.ScaleControl({ unit: 'imperial' }), 'bottom-right');
      map.addControl(
        new mapboxgl.GeolocateControl({
          positionOptions: {
            enableHighAccuracy: true,
          },
          trackUserLocation: true,
        }),
        'top-right',
      );
      map.addControl(new mapboxgl.NavigationControl());
    },
    [center, zoom],
  );

  return (
    <Container>
      <Mapbox
        onStyleLoad={onStyleLoad}
        movingMethod="jumpTo"
        onDragEnd={onMoveEnd}
        onDataLoading={onDataLoading}
        onData={onData}
        onZoomEnd={onMoveEnd}
        containerStyle={containerStyle}
        style={mapStyle === 'light-v10' ? mapStyleJSON : `mapbox://styles/mapbox/${mapStyle}`}
      >
        <Layers
          layers={Object.values(layers)}
          filters={filters}
          tileSourceOptions={tileSourceOptions}
          hasNewLayers={hasNewLayers}
          forceDots={zoom < 12.8}
        />
      </Mapbox>
      <Key layers={layers} selectedSurveyLayers={selectedSurveyLayers} />
      <ToggleSatellite
        onClick={() => dispatch({
          type: 'SET_MAP_STYLE',
          payload: {
            style: mapStyle === 'light-v10' ? 'satellite-v9' : 'light-v10',
          },
        })}
      >
        <FaMap />
      </ToggleSatellite>
    </Container>
  );
};
